import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { PlayCircle, Eye, EyeOff } from "lucide-react";
import { merchantsService } from "@/services/merchants";

const Register = () => {
  const location = useLocation();
  const [name, setName] = useState("");
  const [phone, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [foundFrom, setFoundFrom] = useState("");
  const [courts, setCourts] = useState<any[]>([]);
  const [selectedCourt, setSelectedCourt] = useState("");
  const [passwordMessage, setPasswordMessage] = useState<null | string>(null);
  const [isPasswordMatched, setIsPasswordMatched] = useState<boolean | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  console.log(location.state)
  const fetchCourts = async () => {
    try {
      const res = await merchantsService.fetchMerchantListReg();
      if (res.success) {
        setCourts(res.merchants);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleFoundFromChange = async (value: string) => {
    setFoundFrom(value);

    if (value === "court") {
      await fetchCourts();
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    checkPasswords(value, confirmPassword);
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    checkPasswords(password, value);
  };

  const checkPasswords = (pwd: string, confirmPwd: string) => {
    if (!confirmPwd) {
      setIsPasswordMatched(null);
      setPasswordMessage(null);
      return;
    }

    if (pwd === confirmPwd) {
      setIsPasswordMatched(true);
      setPasswordMessage("Passwords matched!");
    } else {
      setIsPasswordMatched(false);
      setPasswordMessage("The password is not the same!");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 3) {
      toast({
        title: "Invalid Password",
        description: "Password must be at least 3 characters",
        variant: "destructive"
      })
      return;
    }

    if (name.length > 60) {
      toast({
        title: "Invalid Name",
        description: "Name must be lower than 60 characters",
        variant: "destructive"
      })
      return;
    }

    setIsLoading(true);

    try {
      const success = await register(
        name,
        email,
        password,
        phone,
        "0",
        "0",
        foundFrom,
        selectedCourt
      );

      if (success.success) {
        toast({
          title: "Account created!",
          description: "Welcome to Court Replay.",
        });

        const redirectPath = location.state?.from || "/";

        if (redirectPath != "/") {
          if (location.state?.from === "videos") {
            navigate(location.state.path);
          }
          else {
            navigate(redirectPath, { replace: true });
          }
        } else {
          navigate("/landing");
        }

        return;
      } else {
        toast({
          title: "Registration failed",
          description: success.message,
          variant: "destructive",
        });
        return;
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
      return;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-10">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-600 via-emerald-500 to-lime-400" />
      <div className="absolute inset-0 bg-black/30" />

      <div className="relative w-full max-w-md">
        {/* Branding */}
        <div className="text-center mb-10">
          <div
            className="flex items-center justify-center gap-3 mb-3 cursor-pointer hover:opacity-80 transition"
            onClick={() => navigate("/landing")}
          >
            <PlayCircle className="w-10 h-10 text-white drop-shadow" />
            <h1 className="text-3xl font-bold text-white tracking-tight">
              Court Replay
            </h1>
          </div>
          <p className="text-white/80 text-sm">
            Create your account to get started
          </p>
        </div>

        {/* Card */}
        <Card className="border-0 bg-white/90 backdrop-blur-xl shadow-2xl rounded-2xl">
          <CardHeader className="text-center space-y-1">
            <CardTitle className="text-2xl">Create Account</CardTitle>
            <CardDescription>
              Join Court Replay to access your game videos
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Full Name */}
              <div className="space-y-1.5">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-11 rounded-xl"
                  required
                />
              </div>

              {/* Phone */}
              <div className="space-y-1.5">
                <Label htmlFor="phoneNumber">Phone Number</Label>
                <Input
                  id="phoneNumber"
                  type="text"
                  placeholder="08123456789"
                  value={phone}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9]/g, "");
                    if (value && !value.startsWith("08")) {
                      value = "08" + value.replace(/^0+/, "");
                    }
                    setPhoneNumber(value);
                  }}
                  className="h-11 rounded-xl"
                  required
                  minLength={10}
                  maxLength={13}
                />
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="johndoe@gmail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 rounded-xl"
                  required
                />
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <p className="text-xs text-muted-foreground">
                  Panjang password minimal 3 karakter
                </p>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    className="h-11 rounded-xl pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword">Confirm Password</Label>

                {passwordMessage && (
                  <p
                    className={`text-sm font-medium ${isPasswordMatched ? "text-green-600" : "text-red-600"
                      }`}
                  >
                    {passwordMessage}
                  </p>
                )}

                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={(e) =>
                      handleConfirmPasswordChange(e.target.value)
                    }
                    className={`h-11 rounded-xl pr-10 ${isPasswordMatched === null
                      ? ""
                      : isPasswordMatched
                        ? "border-green-500 focus:border-green-600"
                        : "border-red-500 focus:border-red-600"
                      }`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Where did you find us */}
              <div className="space-y-1.5">
                <Label>Where did you find out about us?</Label>
                <select
                  value={foundFrom}
                  onChange={(e) => handleFoundFromChange(e.target.value)}
                  className="w-full h-11 rounded-xl border px-3"
                  required
                >
                  <option value="">Select option</option>
                  <option value="social_media">Social Media</option>
                  <option value="court">Court</option>
                  <option value="friends">Friends</option>
                  <option value="internet">Internet</option>
                  <option value="others">Others</option>
                </select>
              </div>

              {foundFrom === "court" && (
                <div className="space-y-1.5">
                  <Label>Which Court?</Label>

                  <select
                    value={selectedCourt.id}
                    onChange={(e) => setSelectedCourt(e.target.value)}
                    className="w-full h-11 rounded-xl border px-3"
                    required
                  >
                    <option value="">Select court</option>

                    {courts.map((court) => (
                      <option key={court.id} value={court.id}>
                        {court.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                className="w-full h-11 rounded-xl text-base"
                disabled={isLoading}
              >
                {isLoading ? "Creating account..." : "Create Account"}
              </Button>
            </form>

            {/* Footer */}
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">
                Already have an account?
              </span>{" "}
              <Link
                to="/login"
                state={location.state}
                className="font-medium text-primary hover:underline"
              >
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
